// Public security page (F-SEC-3). An honest write-up of the encryption models
// and their limits — no marketing overclaim. "Zero-knowledge" is used ONLY for
// Model B. This mirrors SECURITY.md; keep the two in sync.

import { Link } from 'react-router';

import logoUrl from '../assets/brizina-logo.svg';
import { IconLock } from '../ui/icons';

export function SecurityPage() {
  return (
    <div className="bz-page bz-prose bz-sec">
      <header className="bz-page-header">
        <Link to="/" className="bz-btn bz-btn-small">
          ← Back
        </Link>
        <img src={logoUrl} alt="Brizina" className="bz-logo bz-sec-logo" />
      </header>

      <div className="bz-sec-hero">
        <span className="bz-sec-badge">
          <IconLock size={14} />
          End-to-end encrypted
        </span>
        <h1>Security</h1>
        <p className="bz-sec-lead">
          Brizina is a self-hosted video meeting product with genuine end-to-end encryption (E2EE).
          This page describes exactly what that protects, and — just as importantly — what it does
          not. We would rather be precise than impressive.
        </p>
      </div>

      <h2>What is end-to-end encrypted</h2>
      <p>
        Audio, video, screen share, chat, reactions, and shared files are encrypted on the sending
        device and decrypted only on receiving devices. The media server, relays, network
        intermediaries, and storage layer only ever handle ciphertext. Every media frame transits
        our infrastructure as opaque bytes.
      </p>

      <h2>The two encryption models</h2>
      <div className="bz-sec-grid">
        <section className="bz-sec-card">
          <h3>
            Model A <span className="bz-sec-tag">Default</span>
          </h3>
          <p>
            Our backend generates a random per-session secret, kept in replicated memory only (never
            written to disk or logs) and released to each attendee only when the host admits them. It
            is deleted when the meeting ends. Per-session media keys are derived with{' '}
            <code>HKDF(secret, sessionId)</code>, so reusing an invite link never reuses keys.
          </p>
          <p>
            <strong>What this trusts:</strong> the backend knows the secret for the duration of the
            call. Model A protects you against a compromised media path or infrastructure — but not
            against a malicious or compromised backend. We do <em>not</em> call Model A
            &ldquo;zero-knowledge,&rdquo; because it isn&rsquo;t.
          </p>
        </section>
        <section className="bz-sec-card">
          <h3>
            Model B <span className="bz-sec-tag bz-sec-tag-accent">Zero-knowledge</span>
          </h3>
          <p>
            The passphrase is generated in your browser and travels only in the invite link&rsquo;s{' '}
            <code>#k=</code> fragment, which browsers never send to any server. No server component
            ever sees it. This is the only mode we call zero-knowledge. The trade-off: if the
            passphrase is lost, nobody (including us) can recover the meeting, and a wrong passphrase
            means you simply can&rsquo;t decrypt — the app tells you so clearly.
          </p>
        </section>
      </div>

      <h2>What Brizina does NOT protect against</h2>
      <ul>
        <li>A compromised device belonging to a participant.</li>
        <li>
          A malicious participant — anyone admitted holds the key by definition and can capture what
          they see and hear.
        </li>
        <li>The backend in Model A (session-scoped, as described above).</li>
        <li>
          Metadata: who met whom, when, and for how long. Signaling necessarily reveals this, and we
          keep an audit trail of access grants for the meeting organizer and admins.
        </li>
        <li>
          Forward secrecy within a session: v1 uses a shared room key that is not rotated when a
          participant leaves (per-participant key rotation is planned for a later version).
        </li>
      </ul>

      <h2>Recording, transcription, broadcast</h2>
      <p>
        Server-side recording, broadcast, and server-side transcription are structurally impossible
        in an E2EE meeting, so they are absent by design. Recording is client-side: the host
        captures a local composite of the already-decrypted media, and every attendee sees a
        persistent, non-dismissable recording indicator. If the host uploads a copy, it is encrypted
        in the browser first — the server stores only ciphertext and never holds the key.
      </p>
    </div>
  );
}
